import React, { useState, useEffect, useMemo, useCallback, useImperativeHandle, forwardRef } from 'react';
import { employeeApi } from '../../../api/employeeApi';
import { useSafeClose } from '../../../hooks/useSafeClose';

/**
 * CertificateView — Nachweise-Ansicht
 * Props:
 *   employees              - Array of all employee objects
 *   onUpdate               - callback to reload parent data
 *   externalSearch         - Search string from parent
 *   externalFilterStatus   - Status filter from parent
 *   externalFilterTypeId   - Type filter from parent
 */
const CertificateView = forwardRef(({ 
    employees, 
    documents: passedDocuments = [],
    documentTypes: passedDocumentTypes = [],
    onUpdate, 
    externalSearch = '', 
    externalFilterStatus = [], 
    externalFilterTypeId = [],
    externalFilterWorkerId = []
}, ref) => {
    const documents = passedDocuments;
    const documentTypes = passedDocumentTypes;
    const [loading, setLoading] = useState(false);
    const [selectedWorkerId, setSelectedWorkerId] = useState(null);

    // Modals
    const [showDocTypeModal, setShowDocTypeModal] = useState(false);
    const [showDocModal, setShowDocModal] = useState(false);
    const [editingDoc, setEditingDoc] = useState(null);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        handleAddDoc: (workerId = null) => handleAddDoc(workerId || selectedWorkerId),
        handleEditDoc: (doc) => handleEditDoc(doc),
        setShowDocTypeModal: (val) => setShowDocTypeModal(val),
        setSelectedWorkerId: (id) => setSelectedWorkerId(id),
        getDocuments: () => documents
    }));

    /* Internal loading removed as it's handled by parent */

    // ---- STATUS LOGIC ----
    const getDocStatus = useCallback((doc) => {
        if (!doc.expiry_date) return { type: 'valid', label: 'Gültig', color: '#34c759' };
        const now = new Date();
        const expiry = new Date(doc.expiry_date);
        const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
        const warningDays = doc.warning_days_override ?? doc.warning_days_default ?? 60;

        if (diffDays < 0) return { type: 'expired', label: 'Abgelaufen', color: '#ff3b30', days: diffDays };
        if (diffDays <= warningDays) return { type: 'warning', label: `Läuft ab in ${diffDays}d`, color: '#ff9500', days: diffDays };
        return { type: 'valid', label: 'Gültig', color: '#34c759', days: diffDays };
    }, []);


    // --- URGENCY DATA REMOVED (Now in Header/Modal) ---

    const [sortConfig, setSortConfig] = useState({ key: 'problems', direction: 'desc' });

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // ---- PER-EMPLOYEE SUMMARY ----
    const employeeSummaries = useMemo(() => {
        const map = {};
        employees.forEach(emp => {
            map[emp.id] = { employee: emp, docs: [], expired: 0, warning: 0, valid: 0, total: 0 };
        });

        documents.forEach(doc => {
            if (!map[doc.worker_id]) return;
            const status = getDocStatus(doc);
            map[doc.worker_id].docs.push({ ...doc, status });
            map[doc.worker_id].total++;
            if (status.type === 'expired') map[doc.worker_id].expired++;
            else if (status.type === 'warning') map[doc.worker_id].warning++;
            else map[doc.worker_id].valid++;
        });

        let result = Object.values(map);

        // Search filter
        if (externalSearch.trim()) {
            const lower = externalSearch.toLowerCase();
            result = result.filter(s =>
                s.employee.name.toLowerCase().includes(lower) ||
                s.docs.some(d => (d.type_name || '').toLowerCase().includes(lower))
            );
        }

        // Status filter (Multi-select)
        const allStatusesSelected = externalFilterStatus.length >= 3;
        if (!allStatusesSelected) {
            const selectedSet = new Set(externalFilterStatus);
            result = result.filter(s => s.docs.some(d => selectedSet.has(d.status.type)));
        }

        // Type filter (Multi-select)
        const allTypesSelected = documentTypes.length === 0 || externalFilterTypeId.length >= documentTypes.length;
        if (!allTypesSelected) {
            const selectedSet = new Set(externalFilterTypeId.map(id => String(id)));
            result = result.filter(s => s.docs.some(d => selectedSet.has(String(d.document_type_id))));
        }

        // Worker filter (Multi-select)
        if (employees.length > 0 && externalFilterWorkerId.length < employees.length) {
            const selectedSet = new Set(externalFilterWorkerId.map(id => String(id)));
            result = result.filter(s => selectedSet.has(String(s.employee.id)));
        }

        // Sorting logic
        const sorted = [...result].sort((a, b) => {
            let valA, valB;
            
            if (sortConfig.key === 'name') {
                valA = a.employee.name;
                valB = b.employee.name;
            } else if (sortConfig.key === 'role') {
                valA = a.employee.category_name || '';
                valB = b.employee.category_name || '';
            } else if (sortConfig.key === 'problems') {
                valA = (a.expired * 1000) + (a.warning * 10) + a.valid; // Expired are most important
                valB = (b.expired * 1000) + (b.warning * 10) + b.valid;
            } else if (sortConfig.key.startsWith('type_')) {
                const targetTypeId = sortConfig.key.replace('type_', '');
                const docA = a.docs.find(d => String(d.document_type_id) === String(targetTypeId));
                const docB = b.docs.find(d => String(d.document_type_id) === String(targetTypeId));
                
                // If no doc, use a very small value to put them at the "bottom" of the timeline.
                // Aufsteigend: -1 (Kein Doc), Alt, Neu
                // Absteigend: Neu, Alt, -1 (Kein Doc)
                const noDocVal = -1;
                valA = docA ? new Date(docA.expiry_date).getTime() : noDocVal;
                valB = docB ? new Date(docB.expiry_date).getTime() : noDocVal;

                // Handle invalid dates if any
                if (docA && isNaN(valA)) valA = noDocVal;
                if (docB && isNaN(valB)) valB = noDocVal;
            }

            if (valA === valB) {
                return a.employee.name.localeCompare(b.employee.name);
            }

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [employees, documents, externalSearch, externalFilterStatus, externalFilterTypeId, externalFilterWorkerId, getDocStatus, sortConfig, documentTypes]);

    // ---- AVATAR HELPERS ----
    const getAvatarColor = (name) => {
        const colors = ['#007aff', '#5856d6', '#af52de', '#ff2d55', '#ff9500', '#34c759', '#5ac8fa', '#ff6482', '#30b0c7'];
        let hash = 0;
        for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
        return colors[Math.abs(hash) % colors.length];
    };
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // ---- DETAIL VIEW ----
    const selectedSummary = selectedWorkerId ? employeeSummaries.find(s => s.employee.id === selectedWorkerId) : null;

    const handleAddDoc = (workerId) => {
        setEditingDoc({ worker_id: workerId || null, isNew: true });
        setShowDocModal(true);
    };

    const handleEditDoc = (doc) => {
        setEditingDoc(doc);
        setShowDocModal(true);
    };

    const handleDeleteDoc = async (docId) => {
        if (!window.confirm('Nachweis wirklich löschen?')) return;
        try {
            await employeeApi.deleteDocument(docId);
            await onUpdate();
        } catch (err) {
            alert('Fehler beim Löschen');
        }
    };

    const handleDeleteAttachment = async (docId, attId) => {
        if (!window.confirm('Anhang wirklich löschen?')) return;
        try {
            await employeeApi.deleteDocumentAttachment(docId, attId);
            await onUpdate();
        } catch (err) {
            alert('Fehler beim Löschen');
        }
    };

    // ---- RENDER ----
    if (loading) {
        return (
            <div className="employee-empty-state">
                <div className="employee-empty-state-icon">⏳</div>
                <h3>Lade Nachweise...</h3>
            </div>
        );
    }

    return (
        <div className="cert-view">
            {/* URGENCY BANNER REMOVED (Now in Header/Modal) */}

            {/* MAIN CONTENT: LIST or DETAIL */}
            {selectedWorkerId && selectedSummary ? (
                <div className="cert-detail-view">
                    <button className="cert-back-btn" onClick={() => setSelectedWorkerId(null)}>
                        ← Zurück zur Übersicht
                    </button>

                    <div className="cert-detail-header">
                        <div className="cert-detail-avatar" style={{ background: selectedSummary.employee.color || getAvatarColor(selectedSummary.employee.name) }}>
                            {getInitials(selectedSummary.employee.name)}
                        </div>
                        <div className="cert-detail-info">
                            <h2 className="cert-detail-name">{selectedSummary.employee.name}</h2>
                            <span className="cert-detail-role">{selectedSummary.employee.category_name || 'Keine Rolle'}</span>
                            <span className="cert-detail-count">{selectedSummary.total} Nachweise</span>
                        </div>
                    </div>

                    <div className="cert-detail-cards">
                        {selectedSummary.docs.length === 0 ? (
                            <div className="cert-empty-detail">Keine Nachweise hinterlegt.</div>
                        ) : (
                            selectedSummary.docs.map(doc => (
                                <div key={doc.id} className={`cert-doc-card ${doc.status.type}`}>
                                    <div className="cert-doc-card-header">
                                        <span className="cert-doc-type-name">{doc.type_name}</span>
                                        <span className="cert-doc-status-badge" style={{ background: doc.status.color }}>
                                            {doc.status.label}
                                        </span>
                                    </div>
                                    <div className="cert-doc-card-body">
                                        <div className="cert-doc-meta">
                                            <div className="cert-doc-meta-item">
                                                <span className="cert-doc-meta-label">Ausgestellt</span>
                                                <span className="cert-doc-meta-value">{formatDate(doc.issue_date)}</span>
                                            </div>
                                            <div className="cert-doc-meta-item">
                                                <span className="cert-doc-meta-label">Ablauf</span>
                                                <span className="cert-doc-meta-value">{formatDate(doc.expiry_date)}</span>
                                            </div>
                                            <div className="cert-doc-meta-item">
                                                <span className="cert-doc-meta-label">Warnung ab</span>
                                                <span className="cert-doc-meta-value">{doc.warning_days_override ?? doc.warning_days_default ?? 60} Tage vorher</span>
                                            </div>
                                            <div className="cert-doc-meta-item">
                                                <span className="cert-doc-meta-label">Benachrichtigung</span>
                                                <span className="cert-doc-meta-value">{doc.notification_days_override ?? doc.notification_days_default ?? 30} Tage vorher</span>
                                            </div>
                                        </div>
                                        {doc.notes && <div className="cert-doc-notes">{doc.notes}</div>}

                                        {/* Attachments */}
                                        {doc.attachments && doc.attachments.length > 0 && (
                                            <div className="cert-doc-attachments">
                                                <span className="cert-doc-att-label">📎 Anhänge:</span>
                                                {doc.attachments.map(att => (
                                                    <div key={att.id} className="cert-doc-att-item">
                                                        {att.file_type === 'image' ? (
                                                            <a href={att.public_url} target="_blank" rel="noreferrer" className="cert-doc-att-link">
                                                                🖼️ {att.file_name}
                                                            </a>
                                                        ) : (
                                                            <a href={att.public_url} target="_blank" rel="noreferrer" className="cert-doc-att-link">
                                                                📄 {att.file_name}
                                                            </a>
                                                        )}
                                                        <button className="cert-doc-att-delete" onClick={() => handleDeleteAttachment(doc.id, att.id)} title="Anhang löschen">×</button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="cert-doc-card-actions">
                                        <button className="cert-card-action-btn" onClick={() => handleEditDoc(doc)}>✏️ Bearbeiten</button>
                                        <button className="cert-card-action-btn danger" onClick={() => handleDeleteDoc(doc.id)}>🗑️ Löschen</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                /* TABULAR OVERVIEW */
                <div className="cert-table-container">
                    <table className="cert-table">
                        <thead>
                            <tr>
                                <th className={`sticky-col sortable ${sortConfig.key === 'name' ? 'active' : ''}`} onClick={() => handleSort('name')}>
                                    <div className="th-content">
                                        Mitarbeiter
                                        <span className="sort-indicator">{sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                                    </div>
                                </th>
                                <th className={`sortable ${sortConfig.key === 'role' ? 'active' : ''}`} onClick={() => handleSort('role')}>
                                    <div className="th-content">
                                        Kategorie
                                        <span className="sort-indicator">{sortConfig.key === 'role' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                                    </div>
                                </th>
                                {documentTypes.map(type => {
                                    const isSorted = sortConfig.key === `type_${type.id}`;
                                    return (
                                        <th key={type.id} className={`cert-type-header sortable ${isSorted ? 'active' : ''}`} onClick={() => handleSort(`type_${type.id}`)}>
                                            <div className="th-content">
                                                {type.name}
                                                <span className="sort-indicator">{isSorted ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {employeeSummaries.length === 0 ? (
                                <tr>
                                    <td colSpan={2 + documentTypes.length} style={{ textAlign: 'center', padding: '40px' }}>
                                        <div className="employee-empty-state">
                                            <div className="employee-empty-state-icon">📋</div>
                                            <h3>Keine Ergebnisse</h3>
                                            <p>Versuche einen anderen Filter oder Suchbegriff.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                employeeSummaries.map(summary => (
                                    <tr 
                                        key={summary.employee.id} 
                                        className="cert-table-row"
                                        onClick={() => setSelectedWorkerId(summary.employee.id)}
                                    >
                                        <td className="sticky-col">
                                            <div className="cert-table-name-cell">
                                                <div className="cert-table-avatar" style={{ background: summary.employee.color || getAvatarColor(summary.employee.name) }}>
                                                    {getInitials(summary.employee.name)}
                                                </div>
                                                <span className="cert-table-name">{summary.employee.name}</span>
                                            </div>
                                        </td>
                                        <td>{summary.employee.category_name || '—'}</td>
                                        
                                        {documentTypes.map(type => {
                                            const doc = summary.docs.find(d => String(d.document_type_id) === String(type.id));
                                            return (
                                                <td key={type.id} className="cert-doc-cell">
                                                    {doc ? (
                                                        <div className="cert-cell-status">
                                                            <div className="cert-cell-status-main">
                                                                <span className="status-dot-mini" style={{ background: doc.status.color }}></span>
                                                                <span className="cell-status-label" style={{ color: doc.status.color }}>
                                                                    {doc.status.type === 'expired' ? 'Abgelaufen' : 
                                                                     doc.status.type === 'warning' ? `${doc.status.days} Tage` : 'OK'}
                                                                </span>
                                                            </div>
                                                            <div className="cert-cell-date">{formatDate(doc.expiry_date)}</div>
                                                        </div>
                                                    ) : (
                                                        <span className="cert-cell-empty">—</span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* MODALS */}
            {showDocTypeModal && (
                <DocumentTypeModal
                    types={documentTypes}
                    onClose={() => setShowDocTypeModal(false)}
                    onUpdate={onUpdate}
                />
            )}

            {showDocModal && (
                <EmployeeDocumentModal
                    doc={editingDoc}
                    employees={employees}
                    documentTypes={documentTypes}
                    onClose={() => { setShowDocModal(false); setEditingDoc(null); }}
                    onUpdate={onUpdate}
                />
            )}
        </div>
    );
});

// ========================================
// DOCUMENT TYPE MODAL
// ========================================
function DocumentTypeModal({ types, onClose, onUpdate }) {
    const [list, setList] = useState(types);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ 
        name: '', 
        description: '', 
        interval_months: 12, 
        warning_days_default: 60, 
        notification_days_default: 30,
        is_recurring: true
    });

    const resetForm = () => setForm({ 
        name: '', 
        interval_months: 12, 
        description: '', 
        warning_days_default: 60, 
        notification_days_default: 30,
        is_recurring: true
    });

    const handleSave = async () => {
        try {
            const dataToSave = { ...form };
            if (!dataToSave.is_recurring) {
                dataToSave.interval_months = null;
            }

            if (editing) {
                await employeeApi.updateDocumentType(editing, dataToSave);
            } else {
                await employeeApi.createDocumentType(dataToSave);
            }
            const updated = await employeeApi.getDocumentTypes();
            setList(updated);
            setEditing(null);
            resetForm();
            onUpdate();
        } catch (err) {
            alert('Fehler beim Speichern');
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Möchtest du die Nachweisart "${name}" wirklich löschen? Alle zugehörigen Dokumente bei allen Mitarbeitern werden ebenfalls entfernt.`)) {
            return;
        }
        try {
            await employeeApi.deleteDocumentType(id, true);
            const updated = await employeeApi.getDocumentTypes();
            setList(updated);
            onUpdate();
        } catch (err) {
            if (err.response?.status === 409) {
                if (window.confirm(err.response.data.message)) {
                    await employeeApi.deleteDocumentType(id, true);
                    const updated = await employeeApi.getDocumentTypes();
                    setList(updated);
                    onUpdate();
                }
            } else {
                alert('Fehler beim Löschen');
            }
        }
    };

    const startEdit = (type) => {
        setEditing(type.id);
        setForm({
            name: type.name,
            description: type.description || '',
            interval_months: type.interval_months || '',
            warning_days_default: type.warning_days_default || 60,
            notification_days_default: type.notification_days_default || 30,
            is_recurring: !!type.interval_months
        });
    };

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    return (
        <div className="employee-modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="cert-modal" onClick={e => e.stopPropagation()}>
                <div className="cert-modal-header">
                    <h2>Nachweisarten verwalten</h2>
                    <button className="cert-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="cert-modal-body">
                    {/* Form */}
                    <div className="cert-type-form">
                        <div className="cert-type-form-header">
                            {editing ? 'Nachweisart bearbeiten' : 'Neue Nachweisart erstellen'}
                        </div>
                        <div className="cert-type-form-grid">
                            <div className="cert-form-group">
                                <label>Bezeichnung</label>
                                <input
                                    className="cert-input"
                                    placeholder="z.B. Sicherheitsunterweisung"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                />
                            </div>
                            <div className="cert-form-group">
                                <label>Beschreibung (optional)</label>
                                <input
                                    className="cert-input"
                                    placeholder="Kurze Beschreibung..."
                                    value={form.description}
                                    onChange={e => setForm({ ...form, description: e.target.value })}
                                />
                            </div>

                            <div className="cert-form-group">
                                <div className="cert-toggle-group" style={{ marginTop: '24px' }}>
                                    <div className="cert-toggle-label">
                                        <span className="cert-toggle-title">Befristet</span>
                                    </div>
                                    <label className="optimox-switch">
                                        <input 
                                            type="checkbox" 
                                            checked={form.is_recurring}
                                            onChange={e => setForm({ ...form, is_recurring: e.target.checked })}
                                        />
                                        <span className="optimox-switch-slider"></span>
                                    </label>
                                </div>
                            </div>
                            <div className={`cert-form-group ${!form.is_recurring ? 'disabled' : ''}`}>
                                <label>Gültigkeitszeitraum (Monate)</label>
                                <input
                                    className="cert-input"
                                    type="number"
                                    min="0"
                                    placeholder="12"
                                    value={form.is_recurring ? form.interval_months : ''}
                                    onChange={e => setForm({ ...form, interval_months: e.target.value })}
                                    disabled={!form.is_recurring}
                                />
                            </div>

                            <div className={`cert-form-group ${!form.is_recurring ? 'disabled' : ''}`}>
                                <label>Warnung ab (Tage)</label>
                                <input
                                    className="cert-input"
                                    type="number"
                                    min="0"
                                    value={form.warning_days_default}
                                    onChange={e => setForm({ ...form, warning_days_default: parseInt(e.target.value) || 0 })}
                                    disabled={!form.is_recurring}
                                />
                            </div>
                            <div className={`cert-form-group ${!form.is_recurring ? 'disabled' : ''}`}>
                                <label>Meldung ab (Tage)</label>
                                <input
                                    className="cert-input"
                                    type="number"
                                    min="0"
                                    value={form.notification_days_default}
                                    onChange={e => setForm({ ...form, notification_days_default: parseInt(e.target.value) || 0 })}
                                    disabled={!form.is_recurring}
                                />
                            </div>
                        </div>
                        <div className="cert-type-form-actions">
                            {editing && (
                                <button className="cert-btn secondary" onClick={() => { setEditing(null); resetForm(); }}>
                                    Abbrechen
                                </button>
                            )}
                            <button className="cert-btn primary" onClick={handleSave} disabled={!form.name.trim()}>
                                {editing ? 'Aktualisieren' : '+ Nachweisart erstellen'}
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    <div className="cert-type-list">
                        <div className="cert-list-header">Vorhandene Nachweisarten</div>
                        {list.length === 0 ? (
                            <div className="cert-type-empty">Noch keine Nachweisarten angelegt.</div>
                        ) : list.map(t => (
                            <div key={t.id} className={`cert-type-item ${editing === t.id ? 'active' : ''}`}>
                                <div className="cert-type-item-main">
                                    <div className="cert-type-item-top">
                                        <span className="cert-type-item-name">{t.name}</span>
                                        {t.interval_months && <span className="cert-type-badge">{t.interval_months} Mon.</span>}
                                    </div>
                                    <div className="cert-type-item-meta">
                                        <span className="meta-item">⚠️ {t.warning_days_default} Tage</span>
                                        <span className="meta-divider">•</span>
                                        <span className="meta-item">🔔 {t.notification_days_default} Tage</span>
                                        {t.description && (
                                            <>
                                                <span className="meta-divider">•</span>
                                                <span className="meta-desc">{t.description}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="cert-type-item-actions">
                                    <button className="cert-type-action edit" onClick={() => startEdit(t)} title="Bearbeiten">✏️</button>
                                    <button className="cert-type-action delete" onClick={() => handleDelete(t.id, t.name)} title="Löschen">🗑️</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ========================================
// EMPLOYEE DOCUMENT MODAL
// ========================================
function EmployeeDocumentModal({ doc, employees, documentTypes, onClose, onUpdate }) {
    const isNew = doc?.isNew;
    const [form, setForm] = useState({
        worker_id: doc?.worker_id || '',
        document_type_id: doc?.document_type_id || '',
        issue_date: doc?.issue_date ? doc.issue_date.split('T')[0] : '',
        expiry_date: doc?.expiry_date ? doc.expiry_date.split('T')[0] : '',
        notes: doc?.notes || '',
        warning_days_override: doc?.warning_days_override ?? '',
        notification_days_override: doc?.notification_days_override ?? '',
        has_expiry: doc?.isNew ? true : !!doc?.expiry_date
    });
    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef(null);

    // Auto-calculate expiry based on type interval
    const handleTypeChange = (typeId) => {
        setForm(prev => {
            const type = documentTypes.find(t => String(t.id) === String(typeId));
            const updates = { document_type_id: typeId };
            
            if (type) {
                // Auto-fill thresholds if it's a new document or currently empty
                if (isNew || !prev.warning_days_override) {
                    updates.warning_days_override = type.warning_days_default || 60;
                }
                if (isNew || !prev.notification_days_override) {
                    updates.notification_days_override = type.notification_days_default || 30;
                }

                updates.has_expiry = !!type.interval_months;

                if (type.interval_months && prev.issue_date && updates.has_expiry) {
                    const issue = new Date(prev.issue_date);
                    issue.setMonth(issue.getMonth() + type.interval_months);
                    updates.expiry_date = issue.toISOString().split('T')[0];
                } else if (!updates.has_expiry) {
                    updates.expiry_date = '';
                }
            }
            return { ...prev, ...updates };
        });
    };

    const handleIssueDateChange = (date) => {
        setForm(prev => {
            const type = documentTypes.find(t => String(t.id) === String(prev.document_type_id));
            if (type && type.interval_months && date && prev.has_expiry) {
                const issue = new Date(date);
                issue.setMonth(issue.getMonth() + type.interval_months);
                return { ...prev, issue_date: date, expiry_date: issue.toISOString().split('T')[0] };
            }
            return { ...prev, issue_date: date };
        });
    };

    const handleSave = async () => {
        try {
            setUploading(true);
            let savedDocId;
            const dataToSave = { ...form };
            if (!dataToSave.has_expiry) {
                dataToSave.expiry_date = null;
                dataToSave.warning_days_override = null;
                dataToSave.notification_days_override = null;
            }

            if (isNew) {
                const result = await employeeApi.createDocument(dataToSave);
                savedDocId = result.id;
            } else {
                await employeeApi.updateDocument(doc.id, dataToSave);
                savedDocId = doc.id;
            }

            // Upload pending files
            if (pendingFiles.length > 0 && savedDocId) {
                const formData = new FormData();
                pendingFiles.forEach(f => formData.append('files', f));
                await employeeApi.addDocumentAttachments(savedDocId, formData);
            }

            onUpdate();
            onClose();
        } catch (err) {
            alert('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
        } finally {
            setUploading(false);
        }
    };

    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    return (
        <div className="employee-modal-overlay" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="cert-modal" onClick={e => e.stopPropagation()}>
                <div className="cert-modal-header">
                    <h2>{isNew ? 'Nachweis hinzufügen' : 'Nachweis bearbeiten'}</h2>
                    <button className="cert-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="cert-modal-body">
                    <div className="cert-form-grid">
                        {/* Worker Select */}
                        <div className="cert-form-group">
                            <label>Mitarbeiter</label>
                            <select
                                className="cert-input"
                                value={form.worker_id}
                                onChange={e => setForm({ ...form, worker_id: e.target.value })}
                                disabled={!isNew && !!doc?.worker_id}
                            >
                                <option value="">Mitarbeiter wählen...</option>
                                {employees.map(emp => (
                                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Type Select */}
                        <div className="cert-form-group">
                            <label>Nachweisart</label>
                            <select
                                className="cert-input"
                                value={form.document_type_id}
                                onChange={e => handleTypeChange(e.target.value)}
                            >
                                <option value="">Nachweisart wählen...</option>
                                {documentTypes.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}{t.interval_months ? ` (${t.interval_months} Mon.)` : ''}</option>
                                ))}
                            </select>
                        </div>

                        {/* Dates */}
                        <div className="cert-form-group" style={{ marginTop: '57px' }}>
                            <label>Ausstellungsdatum</label>
                            <input
                                className="cert-input"
                                type="date"
                                value={form.issue_date}
                                onChange={e => handleIssueDateChange(e.target.value)}
                            />
                        </div>

                        <div className="cert-form-group">
                            <label>Ablaufdatum</label>
                            <div className="cert-toggle-group" style={{ marginBottom: '8px' }}>
                                <div className="cert-toggle-label">
                                    <span className="cert-toggle-title">Befristet</span>
                                </div>
                                <label className="optimox-switch">
                                    <input 
                                        type="checkbox" 
                                        checked={form.has_expiry}
                                        onChange={e => setForm({ ...form, has_expiry: e.target.checked, expiry_date: e.target.checked ? form.expiry_date : '' })}
                                    />
                                    <span className="optimox-switch-slider"></span>
                                </label>
                            </div>
                            <div className={`${!form.has_expiry ? 'disabled' : ''}`}>
                                <input
                                    className="cert-input"
                                    type="date"
                                    value={form.has_expiry ? form.expiry_date : ''}
                                    onChange={e => setForm({ ...form, expiry_date: e.target.value })}
                                    disabled={!form.has_expiry}
                                />
                            </div>
                        </div>

                        {/* Thresholds */}
                        <div className={`cert-form-group ${!form.has_expiry ? 'disabled' : ''}`}>
                            <label>Warnung ab (Tage)</label>
                            <input
                                className="cert-input"
                                type="number"
                                placeholder="60"
                                value={form.has_expiry ? form.warning_days_override : ''}
                                onChange={e => setForm({ ...form, warning_days_override: e.target.value })}
                                disabled={!form.has_expiry}
                            />
                        </div>
                        <div className={`cert-form-group ${!form.has_expiry ? 'disabled' : ''}`}>
                            <label>Benachrichtigung ab (Tage)</label>
                            <input
                                className="cert-input"
                                type="number"
                                placeholder="30"
                                value={form.has_expiry ? form.notification_days_override : ''}
                                onChange={e => setForm({ ...form, notification_days_override: e.target.value })}
                                disabled={!form.has_expiry}
                            />
                        </div>

                        {/* Notes */}
                        <div className="cert-form-group full">
                            <label>Notizen</label>
                            <textarea
                                className="cert-input"
                                rows={3}
                                value={form.notes}
                                onChange={e => setForm({ ...form, notes: e.target.value })}
                                placeholder="Optionale Notizen..."
                            />
                        </div>

                        {/* File Upload */}
                        <div className="cert-form-group full">
                            <label>Dateien anhängen</label>
                            <div
                                className="cert-upload-zone"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    multiple
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    accept="image/*,application/pdf,.doc,.docx"
                                    onChange={e => {
                                        if (e.target.files) setPendingFiles(prev => [...prev, ...Array.from(e.target.files)]);
                                    }}
                                />
                                📎 Dateien auswählen (PDF, Bilder, Dokumente)
                            </div>
                            {pendingFiles.length > 0 && (
                                <div className="cert-pending-files">
                                    {pendingFiles.map((f, i) => (
                                        <div key={i} className="cert-pending-file">
                                            <span>{f.name}</span>
                                            <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}>×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="cert-modal-footer">
                    <button className="cert-btn secondary" onClick={onClose}>Abbrechen</button>
                    <button
                        className="cert-btn primary"
                        onClick={handleSave}
                        disabled={!form.worker_id || !form.document_type_id || uploading}
                    >
                        {uploading ? '⏳ Speichern...' : (isNew ? 'Hinzufügen' : 'Speichern')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CertificateView;
